import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { MallFragment } from "@saleor/fragments/types/MallFragment";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles(
  {
    colArea: {
      width: 175
    },
    colContact: {
      width: 240
    },
    colEmail: {
      width: 350
    },
    colName: {
      width: 270
    },
    colStatus: {
      width: 110
    },
    tableRow: {
      "& td": {
        wordBreak: "break-all"
      },
      cursor: "pointer"
    }
  },
  { name: "MallList" }
);

interface MallListProps extends ListProps {
  malls?: MallFragment[];
  onAdd?();
}

const numberOfColumns = 6;

const MallList: React.FC<MallListProps> = props => {
  const {
    malls,
    disabled,
    settings,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onRowClick
  } = props;

  const classes = useStyles(props);

  const SmopStatus = {
    ACT: "Active",
    DCL: "Declined",
    DEL: "Deleted",
    INA: "Inactive",
    RVW: "Reviewed"
  };

  return (
    <ResponsiveTable>
      <TableHead colSpan={numberOfColumns} disabled={disabled} items={[]}>
        <TableCellHeader className={classes.colName}>
          <FormattedMessage defaultMessage="Mall Name" />
        </TableCellHeader>
        <TableCellHeader className={classes.colContact}>
          <FormattedMessage
            defaultMessage="Contact Person"
            description="number of subcategories"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colEmail}>
          <FormattedMessage
            defaultMessage="Email"
            description="number of subcategories"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colArea}>
          <FormattedMessage
            defaultMessage="Mall Area"
            description="number of subcategories"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colArea}>
          <FormattedMessage
            defaultMessage="Service Area"
            description="number of subcategories"
          />
        </TableCellHeader>
        <TableCellHeader className={classes.colStatus}>
          <FormattedMessage defaultMessage="Status" description="status" />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          malls,
          (mall, index) => (
            <TableRow
              className={classes.tableRow}
              hover={!!mall}
              key={mall ? mall.id : "skeleton"}
              data-test="id"
              data-test-id={maybe(() => mall.id)}
              onClick={mall ? onRowClick(mall.id) : undefined}
            >
              <TableCell
                className={classes.colName}
                data-test="name"
                id={`mallNameRow${index}`}
              >
                {mall && mall.name ? mall.name : <Skeleton />}
              </TableCell>
              <TableCell
                className={classes.colContact}
                data-test="contactPerson"
                id={`contactPersonRow${index}`}
              >
                {mall && mall.contactPerson ? mall.contactPerson : <Skeleton />}
              </TableCell>
              <TableCell
                className={classes.colEmail}
                data-test="email"
                id={`emailRow${index}`}
              >
                {mall && mall.email ? mall.email : <Skeleton />}
              </TableCell>
              <TableCell
                className={classes.colArea}
                data-test="totalLandArea"
                id={`mallAreaRow${index}`}
              >
                {mall && mall.totalLandArea ? mall.totalLandArea : <Skeleton />}
              </TableCell>
              <TableCell
                className={classes.colArea}
                data-test="serviceArea"
                id={`serviceAreaRow${index}`}
              >
                {mall && mall.serviceArea ? `${mall.serviceArea} Km` : "-"}
              </TableCell>
              <TableCell
                className={classes.colStatus}
                data-test="status"
                id={`statusRow${index}`}
              >
                {maybe(() => SmopStatus[mall.status], "-")}
              </TableCell>
            </TableRow>
          ),
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No malls found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

MallList.displayName = "MallList";
export default MallList;
